import { useState, useEffect, useRef } from "react";
import { format, addMonths } from "date-fns";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { getAirports, getFilters } from "@/shared/api/filters";
import type { Airport, SystemDictionary } from "@/shared/api/filters";
import { createOrder, createDraft, getDrafts } from "../../api/orders";
import type {
  Draft,
  FlightLocation,
  CreateOrderPayload,
  CreateDraftPayload,
} from "../../types/order.types";
import { FlightInfoForm } from "./FlightInfoForm";
import { PassengerInfoForm } from "./PassengerInfoForm";
import { DraftsPanel } from "./DraftsPanel";

export interface FlightFormState {
  from: FlightLocation | null;
  to: FlightLocation | null;
  departureDate: Date | undefined;
  arrivalDate: Date | undefined;
  departureTime: string;
  arrivalTime: string;
  flightNumber: string;
  airline: string;
  baggageCheckedKg: string;
  baggageCarryOnKg: string;
  price: string;
}

export interface PassengerFormState {
  name: string;
  surname: string;
  issuingCountry: string;
  gender: "male" | "female" | "";
  birthday: Date | undefined;
  documentId: string;
  serialNumber: string;
  iin: string;
  issuedBy: string;
  issueDate: Date | undefined;
  documentExpiredAt: Date | undefined;
}

const INITIAL_FLIGHT: FlightFormState = {
  from: null,
  to: null,
  departureDate: undefined,
  arrivalDate: undefined,
  departureTime: "",
  arrivalTime: "",
  flightNumber: "",
  airline: "",
  baggageCheckedKg: "",
  baggageCarryOnKg: "",
  price: "",
};

const INITIAL_RETURN_FLIGHT: FlightFormState = { ...INITIAL_FLIGHT };

function combineDateAndTime(date: Date, time: string): Date {
  const [hours, minutes] = time.split(":").map(Number);
  const combined = new Date(date);
  combined.setHours(hours || 0, minutes || 0, 0, 0);
  return combined;
}

const INITIAL_PASSENGER: PassengerFormState = {
  name: "",
  surname: "",
  issuingCountry: "",
  gender: "",
  birthday: undefined,
  documentId: "",
  serialNumber: "",
  iin: "",
  issuedBy: "",
  issueDate: undefined,
  documentExpiredAt: undefined,
};

function hasFlightData(flight: FlightFormState): boolean {
  return !!(
    flight.from ||
    flight.to ||
    flight.departureDate ||
    flight.arrivalDate ||
    flight.departureTime ||
    flight.arrivalTime ||
    flight.flightNumber ||
    flight.airline ||
    flight.price
  );
}

function isPassengerComplete(p: PassengerFormState): boolean {
  if (!p.name || !p.surname || !p.issuingCountry || !p.gender) return false;
  if (!p.birthday || !p.documentId || !p.issuedBy || !p.issueDate || !p.documentExpiredAt) return false;
  if (p.issuingCountry === "KZ" && p.iin.length !== 12) return false;
  return true;
}

function validateForm(
  flight: FlightFormState,
  passengers: PassengerFormState[],
  operator: string,
  phoneNumber: string,
  hasReturnFlight: boolean,
  returnFlight: FlightFormState
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!flight.from) errors.from = "Обязательное поле";
  if (!flight.to) errors.to = "Обязательное поле";
  if (!flight.departureDate) {
    errors.departureDate = "Обязательное поле";
  }
  if (!flight.arrivalDate) {
    errors.arrivalDate = "Обязательное поле";
  } else if (flight.departureDate && flight.arrivalDate < flight.departureDate) {
    errors.arrivalDate = "Дата прилёта не может быть раньше даты вылета";
  }
  if (!flight.departureTime) errors.departureTime = "Обязательное поле";
  if (!flight.arrivalTime) errors.arrivalTime = "Обязательное поле";
  if (!flight.flightNumber) errors.flightNumber = "Обязательное поле";
  if (!flight.airline) errors.airline = "Обязательное поле";
  if (flight.price === "") {
    errors.price = "Обязательное поле";
  } else if (parseFloat(flight.price) < 0) {
    errors.price = "Цена не может быть отрицательной";
  }
  if (flight.baggageCheckedKg !== "" && parseFloat(flight.baggageCheckedKg) < 0) {
    errors.baggageCheckedKg = "Значение не может быть отрицательным";
  }
  if (flight.baggageCarryOnKg !== "" && parseFloat(flight.baggageCarryOnKg) < 0) {
    errors.baggageCarryOnKg = "Значение не может быть отрицательным";
  }

  if (hasReturnFlight) {
    if (!returnFlight.from) errors.return_from = "Обязательное поле";
    if (!returnFlight.to) errors.return_to = "Обязательное поле";
    if (!returnFlight.departureDate) {
      errors.return_departureDate = "Обязательное поле";
    }
    if (!returnFlight.arrivalDate) {
      errors.return_arrivalDate = "Обязательное поле";
    } else if (
      returnFlight.departureDate &&
      returnFlight.arrivalDate < returnFlight.departureDate
    ) {
      errors.return_arrivalDate = "Дата прилёта не может быть раньше даты вылета";
    }
    if (!returnFlight.departureTime) errors.return_departureTime = "Обязательное поле";
    if (!returnFlight.arrivalTime) errors.return_arrivalTime = "Обязательное поле";
    if (!returnFlight.flightNumber) errors.return_flightNumber = "Обязательное поле";
    if (!returnFlight.airline) errors.return_airline = "Обязательное поле";
    if (returnFlight.price === "") {
      errors.return_price = "Обязательное поле";
    } else if (parseFloat(returnFlight.price) <= 0) {
      errors.return_price = "Цена должна быть больше 0";
    }
    if (
      returnFlight.baggageCheckedKg !== "" &&
      parseFloat(returnFlight.baggageCheckedKg) < 0
    ) {
      errors.return_baggageCheckedKg = "Значение не может быть отрицательным";
    }
    if (
      returnFlight.baggageCarryOnKg !== "" &&
      parseFloat(returnFlight.baggageCarryOnKg) < 0
    ) {
      errors.return_baggageCarryOnKg = "Значение не может быть отрицательным";
    }

    if (
      !errors.return_departureDate &&
      !errors.return_departureTime &&
      !errors.arrivalDate &&
      !errors.arrivalTime &&
      flight.arrivalDate &&
      flight.arrivalTime &&
      returnFlight.departureDate &&
      returnFlight.departureTime
    ) {
      const outboundArrival = combineDateAndTime(flight.arrivalDate, flight.arrivalTime);
      const returnDeparture = combineDateAndTime(
        returnFlight.departureDate,
        returnFlight.departureTime
      );
      if (returnDeparture <= outboundArrival) {
        errors.return_departureDate =
          "Вылет обратного рейса должен быть позже прилёта рейса туда";
      }
    }
  }

  passengers.forEach((passenger, index) => {
    const pfx = `passenger_${index}_`;

    if (!passenger.name) errors[`${pfx}name`] = "Обязательное поле";
    if (!passenger.surname) errors[`${pfx}surname`] = "Обязательное поле";
    if (!passenger.issuingCountry) errors[`${pfx}issuingCountry`] = "Обязательное поле";
    if (!passenger.gender) errors[`${pfx}gender`] = "Обязательное поле";
    if (!passenger.birthday) {
      errors[`${pfx}birthday`] = "Обязательное поле";
    } else if (passenger.birthday >= new Date()) {
      errors[`${pfx}birthday`] = "Дата рождения должна быть в прошлом";
    }
    if (!passenger.documentId) {
      errors[`${pfx}documentId`] = "Обязательное поле";
    } else if (
      passenger.issuingCountry === "KZ" &&
      !/^[A-Z]\d{8}$/.test(passenger.documentId)
    ) {
      errors[`${pfx}documentId`] = "Одна латинская буква и 8 цифр (например, N12345678)";
    }
    if (passenger.issuingCountry === "KZ") {
      if (!passenger.iin) {
        errors[`${pfx}iin`] = "ИИН обязателен для граждан Казахстана";
      } else if (!/^\d{12}$/.test(passenger.iin)) {
        errors[`${pfx}iin`] = "ИИН должен содержать ровно 12 цифр";
      }
    }
    if (!passenger.issuedBy) {
      errors[`${pfx}issuedBy`] = "Обязательное поле";
    } else if (!/^[A-Z ]{1,50}$/.test(passenger.issuedBy)) {
      errors[`${pfx}issuedBy`] = "Только латинские буквы в верхнем регистре, не более 50 символов";
    }
    if (!passenger.issueDate) errors[`${pfx}issueDate`] = "Обязательное поле";
    if (!passenger.documentExpiredAt) {
      errors[`${pfx}documentExpiredAt`] = "Обязательное поле";
    } else if (passenger.documentExpiredAt <= new Date()) {
      errors[`${pfx}documentExpiredAt`] = "Срок действия документа истёк";
    } else if (flight.arrivalDate && passenger.documentExpiredAt < addMonths(flight.arrivalDate, 6)) {
      errors[`${pfx}documentExpiredAt`] = "Дата истечения паспорта менее 6 месяцев";
    }
  });

  if (!operator) errors.operator = "Обязательное поле";
  if (!phoneNumber) errors.phoneNumber = "Обязательное поле";

  return errors;
}

interface CreateOrderModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateOrderModal({ open, onClose, onSuccess }: CreateOrderModalProps) {
  const [flightState, setFlightState] = useState<FlightFormState>(INITIAL_FLIGHT);
  const [hasReturnFlight, setHasReturnFlight] = useState(false);
  const [returnFlightState, setReturnFlightState] =
    useState<FlightFormState>(INITIAL_RETURN_FLIGHT);
  const [passengers, setPassengers] = useState<PassengerFormState[]>([{ ...INITIAL_PASSENGER }]);
  const [passengerKeys, setPassengerKeys] = useState<number[]>([0]);
  const nextPassengerKey = useRef(1);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [dictionary, setDictionary] = useState<SystemDictionary | null>(null);
  const [initialDrafts, setInitialDrafts] = useState<Draft[]>([]);
  const [initialTotalPages, setInitialTotalPages] = useState(1);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<Draft | null>(null);
  const [operator, setOperator] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    if (!open) return;
    setFlightState(INITIAL_FLIGHT);
    setHasReturnFlight(false);
    setReturnFlightState(INITIAL_RETURN_FLIGHT);
    setPassengers([{ ...INITIAL_PASSENGER }]);
    setPassengerKeys([0]);
    setOperator("");
    setPhoneNumber("");
    setIsDirty(false);
    setErrors({});
    setIsInitialLoading(true);
    Promise.all([getAirports(), getFilters(), getDrafts(1, 10)])
      .then(([airportsData, dict, draftsRes]) => {
        setAirports(airportsData);
        setDictionary(dict);
        setInitialDrafts(draftsRes.data);
        setInitialTotalPages(draftsRes.totalPages);
      })
      .catch(() => toast.error("Не удалось загрузить данные"))
      .finally(() => setIsInitialLoading(false));
  }, [open]);

  const handleFlightChange = (updates: Partial<FlightFormState>) => {
    setFlightState((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
  };

  const handleReturnFlightChange = (updates: Partial<FlightFormState>) => {
    setReturnFlightState((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
  };

  const addReturnFlight = () => {
    setHasReturnFlight(true);
  };

  const removeReturnFlight = () => {
    setHasReturnFlight(false);
    setReturnFlightState(INITIAL_RETURN_FLIGHT);
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next)
        .filter((key) => key.startsWith("return_"))
        .forEach((key) => delete next[key]);
      return next;
    });
  };

  const handlePassengerChange = (index: number, updates: Partial<PassengerFormState>) => {
    setPassengers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates } as PassengerFormState;
      return next;
    });
    setIsDirty(true);
  };

  const addPassenger = () => {
    setPassengers((prev) => [...prev, { ...INITIAL_PASSENGER }]);
    setPassengerKeys((prev) => [...prev, nextPassengerKey.current++]);
  };

  const removePassenger = (index: number) => {
    setPassengers((prev) => prev.filter((_, i) => i !== index));
    setPassengerKeys((prev) => prev.filter((_, i) => i !== index));
    setErrors({});
  };

  const handleCancel = () => {
    if (!isDirty) {
      onClose();
      return;
    }
    setShowCancelConfirm(true);
  };

  const applyDraft = (draft: Draft) => {
    setFlightState((prev) => ({
      ...prev,
      from: draft.from,
      to: draft.to,
      departureDate: undefined,
      arrivalDate: undefined,
      departureTime: draft.departureAt,
      arrivalTime: draft.arrivalAt,
      flightNumber: draft.flightNumber,
      airline: draft.airline,
      price: String(draft.price.perPassenger),
      baggageCheckedKg:
        draft.additionalInfo?.baggageCheckedKg != null
          ? String(draft.additionalInfo.baggageCheckedKg)
          : "",
      baggageCarryOnKg:
        draft.additionalInfo?.baggageCarryOnKg != null
          ? String(draft.additionalInfo.baggageCarryOnKg)
          : "",
    }));

    if (draft.returnFlight) {
      const returnFlight = draft.returnFlight;
      setHasReturnFlight(true);
      setReturnFlightState((prev) => ({
        ...prev,
        from: returnFlight.from,
        to: returnFlight.to,
        departureDate: undefined,
        arrivalDate: undefined,
        departureTime: format(new Date(returnFlight.departureAt), "HH:mm"),
        arrivalTime: format(new Date(returnFlight.arrivalAt), "HH:mm"),
        flightNumber: returnFlight.flightNumber,
        airline: returnFlight.airline,
        price: String(returnFlight.price.perPassenger),
        baggageCheckedKg:
          returnFlight.additionalInfo?.baggageCheckedKg != null
            ? String(returnFlight.additionalInfo.baggageCheckedKg)
            : "",
        baggageCarryOnKg:
          returnFlight.additionalInfo?.baggageCarryOnKg != null
            ? String(returnFlight.additionalInfo.baggageCarryOnKg)
            : "",
      }));
    } else {
      setHasReturnFlight(false);
      setReturnFlightState(INITIAL_RETURN_FLIGHT);
    }

    setIsDirty(true);
  };

  const handleApplyDraft = (draft: Draft) => {
    if (hasFlightData(flightState)) {
      setPendingDraft(draft);
      return;
    }
    applyDraft(draft);
  };

  const handleSave = async () => {
    const errs = validateForm(
      flightState,
      passengers,
      operator,
      phoneNumber,
      hasReturnFlight,
      returnFlightState
    );
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const departureAt = `${format(flightState.departureDate!, "yyyy-MM-dd")}T${flightState.departureTime}:00+05:00`;
    const arrivalAt = `${format(flightState.arrivalDate!, "yyyy-MM-dd")}T${flightState.arrivalTime}:00+05:00`;

    const additionalInfo: { baggageCheckedKg?: number; baggageCarryOnKg?: number } = {};
    if (flightState.baggageCheckedKg)
      additionalInfo.baggageCheckedKg = parseFloat(flightState.baggageCheckedKg);
    if (flightState.baggageCarryOnKg)
      additionalInfo.baggageCarryOnKg = parseFloat(flightState.baggageCarryOnKg);

    let returnFlight: CreateOrderPayload["returnFlight"];
    if (hasReturnFlight) {
      const returnDepartureAt = `${format(returnFlightState.departureDate!, "yyyy-MM-dd")}T${returnFlightState.departureTime}:00+05:00`;
      const returnArrivalAt = `${format(returnFlightState.arrivalDate!, "yyyy-MM-dd")}T${returnFlightState.arrivalTime}:00+05:00`;

      const returnAdditionalInfo: { baggageCheckedKg?: number; baggageCarryOnKg?: number } = {};
      if (returnFlightState.baggageCheckedKg)
        returnAdditionalInfo.baggageCheckedKg = parseFloat(returnFlightState.baggageCheckedKg);
      if (returnFlightState.baggageCarryOnKg)
        returnAdditionalInfo.baggageCarryOnKg = parseFloat(returnFlightState.baggageCarryOnKg);

      returnFlight = {
        flightNumber: returnFlightState.flightNumber,
        airline: returnFlightState.airline,
        fareType: "standard",
        flightType: "charter",
        class: "economy",
        refundable: false,
        from: returnFlightState.from!,
        to: returnFlightState.to!,
        departureAt: returnDepartureAt,
        arrivalAt: returnArrivalAt,
        price: { perPassenger: parseFloat(returnFlightState.price), currency: "KZT" },
        additionalInfo: returnAdditionalInfo,
      };
    }

    const orderPayload: CreateOrderPayload = {
      flight: {
        flightNumber: flightState.flightNumber,
        airline: flightState.airline,
        fareType: "standard",
        flightType: "charter",
        class: "economy",
        refundable: false,
        from: flightState.from!,
        to: flightState.to!,
        departureAt,
        arrivalAt,
        price: { perPassenger: parseFloat(flightState.price), currency: "KZT" },
        additionalInfo,
      },
      ...(returnFlight ? { returnFlight } : {}),
      operator,
      phoneNumber,
      passengers: passengers.map((p) => ({
        name: p.name,
        surname: p.surname,
        birthday: format(p.birthday!, "yyyy-MM-dd"),
        gender: p.gender as "male" | "female",
        document_type: "passport",
        document_id: p.documentId,
        ...(p.issuingCountry !== "KZ" && p.serialNumber
          ? { serial_number: p.serialNumber }
          : {}),
        ...(p.issuingCountry === "KZ" ? { iin: p.iin } : {}),
        document_expired_at: format(p.documentExpiredAt!, "yyyy-MM-dd"),
        issuing_country: p.issuingCountry,
        issued_by: p.issuedBy,
        issue_date: format(p.issueDate!, "yyyy-MM-dd"),
      })),
    };

    const draftPayload: CreateDraftPayload = {
      flightNumber: flightState.flightNumber,
      airline: flightState.airline,
      fareType: "standard",
      flightType: "charter",
      class: "economy",
      refundable: false,
      from: flightState.from!,
      to: flightState.to!,
      departureAt: flightState.departureTime,
      arrivalAt: flightState.arrivalTime,
      price: { perPassenger: parseFloat(flightState.price), currency: "KZT" },
      additionalInfo,
      ...(returnFlight ? { returnFlight } : {}),
    };

    setIsSaving(true);
    try {
      await createOrder(orderPayload);
      createDraft(draftPayload)
        .then(() => toast.success("Заказ успешно создан"))
        .catch((e) =>
          toast.warning(
            e?.response?.data?.message ?? "Заказ создан, но черновик не удалось сохранить"
          )
        );
      onSuccess();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Ошибка создания заказа");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
        <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
            <DialogTitle>Создание заказа</DialogTitle>
          </DialogHeader>

          {isInitialLoading || !dictionary ? (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex flex-1 min-h-0">
              <div className="flex-[3] overflow-y-auto px-6 py-4 space-y-8">
                <FlightInfoForm
                  state={flightState}
                  errors={errors}
                  onChange={handleFlightChange}
                  airports={airports}
                  dictionary={dictionary}
                />
                {hasReturnFlight ? (
                  <FlightInfoForm
                    title="Информация об обратном рейсе"
                    errorPrefix="return_"
                    state={returnFlightState}
                    errors={errors}
                    onChange={handleReturnFlightChange}
                    airports={airports}
                    dictionary={dictionary}
                    onRemove={removeReturnFlight}
                  />
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addReturnFlight}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Добавить обратный рейс
                  </Button>
                )}
                <div className="space-y-6">
                  <h2 className="text-base font-semibold">2. Информация о пассажире</h2>
                  {passengers.map((passenger, index) => (
                    <PassengerInfoForm
                      key={passengerKeys[index]}
                      index={index}
                      state={passenger}
                      errors={errors}
                      onChange={(updates) => handlePassengerChange(index, updates)}
                      onDelete={passengers.length > 1 ? () => removePassenger(index) : undefined}
                      dictionary={dictionary}
                      arrivalDate={flightState.arrivalDate}
                    />
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addPassenger}
                    disabled={!isPassengerComplete(passengers[passengers.length - 1]!)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Добавить пассажира
                  </Button>
                </div>
                <div className="space-y-4">
                  <h2 className="text-base font-semibold">3. Контактная информация</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label>Номер телефона</Label>
                      <div className="flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                        <span className="select-none text-foreground mr-0.5">+</span>
                        <input
                          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
                          inputMode="numeric"
                          value={phoneNumber}
                          onChange={(e) => {
                            setPhoneNumber(e.target.value.replace(/\D/g, ""));
                            setIsDirty(true);
                          }}
                          placeholder="77001234567"
                        />
                      </div>
                      {errors.phoneNumber && (
                        <p className="text-xs text-destructive">{errors.phoneNumber}</p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label>Оператор</Label>
                      <Input
                        value={operator}
                        onChange={(e) => {
                          setOperator(e.target.value);
                          setIsDirty(true);
                        }}
                        placeholder="Kompas KZ"
                      />
                      {errors.operator && (
                        <p className="text-xs text-destructive">{errors.operator}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-[2] border-l flex flex-col min-h-0">
                <DraftsPanel
                  initialDrafts={initialDrafts}
                  initialTotalPages={initialTotalPages}
                  onApplyDraft={handleApplyDraft}
                />
              </div>
            </div>
          )}

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
              Отмена
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Отменить создание заказа?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Вы уверены, что хотите отменить? Введённые данные будут потеряны.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelConfirm(false)}
            >
              Назад
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowCancelConfirm(false);
                onClose();
              }}
            >
              Продолжить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingDraft !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPendingDraft(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Применить черновик?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Данные рейса будут заменены данными из черновика. Продолжить?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDraft(null)}>
              Отмена
            </Button>
            <Button
              onClick={() => {
                if (pendingDraft) applyDraft(pendingDraft);
                setPendingDraft(null);
              }}
            >
              Продолжить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
